/**
 * THE MOUTH. Plays a spoken reply as it arrives. `synthesize(args)` is the transport the app passes in (the owner's door
 * with text, the customer's with a replyId): it returns a fetch-style Response whose body is raw 24 kHz PCM16 (asked for
 * with format 'pcm'), and receives `signal` so stop() can abort it. Each chunk is scheduled right after the last, so the
 * first sound is the first bytes. An old server's mp3 still plays, whole.
 *
 *   const mouth = createMouth({ synthesize, onState })
 *   mouth.unlock()                                   // inside a tap: iOS only lets sound start from a gesture
 *   await mouth.speak({ key, ...args })              // 'done' | 'stopped' | 'skipped' | 'failed'
 *   mouth.enqueue({ key, ...speakArgs }, { onStart, onResponse })
 *                                                    // the next sentence of the same reply: it never interrupts. Its request starts
 *                                                    // when the sentence before has fully ARRIVED (so its audio cannot overtake it),
 *                                                    // and it plays right after that one ends, with no gap. onStart: when it is heard.
 *   mouth.stop()                                     // interrupt: sound, request, every queued sentence, every promise
 *   mouth.speaking                                   // true while any sound is scheduled
 *
 * ONE SYNTHESIS PER REPLY: a key (the reply's id, else its text) already spoken or being spoken is 'skipped'.
 */
import { getAudioContext } from './sharedAudio';

const RATE = 24000;

export function createMouth({ synthesize, onState, deps = {} } = {}) {
  // The ONE shared context unless a test (or a host with its own) passes a constructor.
  const Ctx = deps.AudioContext || (typeof window !== 'undefined' ? window.AudioContext || window.webkitAudioContext : null);
  let ctx = null;
  let runs = []; // every sentence that is playing or waiting its turn, in order
  let nextAt = 0; // when the next buffer may start: shared by every run, so sentences follow each other with no gap
  let lastReceived = Promise.resolve(); // resolves when the last queued sentence has fully arrived
  const spoken = new Set();
  let speaking = false;
  const setSpeaking = (v) => { if (v !== speaking) { speaking = v; onState?.(v ? 'speaking' : 'idle'); } };

  function unlock() {
    if (!Ctx) return;
    if (!ctx || ctx.state === 'closed') ctx = deps.AudioContext ? new Ctx() : getAudioContext();
    if (ctx.state === 'suspended') { try { ctx.resume(); } catch (_) { /* gone */ } }
  }

  function stop() {
    if (!runs.length) return;
    const all = runs;
    runs = [];
    nextAt = 0;
    lastReceived = Promise.resolve();
    for (const run of all) {
      run.stopped = true;
      try { run.controller.abort(); } catch (_) { /* gone */ }
      for (const s of run.sources) { try { s.stop(); } catch (_) { /* already ended */ } }
      if (run.timer) clearTimeout(run.timer);
      spoken.delete(run.key); // an interrupted reply may be asked for again
      run.markReceived();
      run.settle('stopped');
    }
    setSpeaking(false);
  }

  function begin({ key, ...args } = {}, opts = {}, queued) {
    const k = String(key ?? args.replyId ?? args.text ?? '');
    if (k && (spoken.has(k) || runs.some((r) => r.key === k))) return Promise.resolve('skipped');
    if (!queued) stop();
    if (!Ctx) return Promise.resolve('failed');
    unlock();
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : { signal: {}, abort() {} };
    let settle; const done = new Promise((r) => { settle = r; });
    let markReceived; const received = new Promise((r) => { markReceived = r; });
    const run = { key: k, controller, sources: [], settle, markReceived, stopped: false, timer: null };
    runs.push(run);
    const after = lastReceived;
    lastReceived = received;
    let pending = 0; let streamEnded = false; let finished = false; let started = false;
    const finish = (result) => {
      if (finished) return; finished = true;
      markReceived();
      runs = runs.filter((r) => r !== run);
      if (!runs.length) setSpeaking(false);
      if (result === 'done' && k) spoken.add(k);
      settle(result);
    };
    const maybeDone = () => { if (streamEnded && pending === 0) finish('done'); };
    const play = (buffer) => {
      if (run.stopped) return;
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.connect(ctx.destination);
      nextAt = Math.max(nextAt, ctx.currentTime + 0.04);
      const at = nextAt;
      nextAt = at + buffer.length / buffer.sampleRate;
      pending += 1;
      src.onended = () => { pending -= 1; maybeDone(); };
      run.sources.push(src);
      src.start(at);
      setSpeaking(true);
      if (!started) {
        started = true;
        // Heard when it is scheduled to start, not when it was queued: a caption follows the sound.
        const wait = Math.max(0, (at - ctx.currentTime) * 1000);
        if (opts.onStart) { if (wait < 80) opts.onStart(); else run.timer = setTimeout(() => { if (!run.stopped) opts.onStart(); }, wait); }
      }
    };
    const pcmToBuffer = (bytes) => {
      const n = Math.floor(bytes.length / 2);
      const buf = ctx.createBuffer(1, n, RATE);
      const ch = buf.getChannelData(0);
      const view = new DataView(bytes.buffer, bytes.byteOffset, n * 2);
      for (let i = 0; i < n; i++) ch[i] = view.getInt16(i * 2, true) / 32768;
      return buf;
    };
    const arrived = () => { streamEnded = true; markReceived(); maybeDone(); };
    (async () => {
      try {
        await after; // the sentence before has fully arrived: this one's audio cannot overtake it
        if (run.stopped) return;
        const res = await synthesize({ ...args, signal: controller.signal });
        if (run.stopped) return;
        if (!res || res.ok === false) throw new Error('synthesis failed');
        try { opts.onResponse?.(res); } catch (_) { /* the caller's own */ }
        const type = String(res.headers?.get?.('content-type') || '').toLowerCase();
        if (!type.includes('pcm') && !type.includes('L16')) {
          // An old server: the whole mp3, decoded and played once.
          const decoded = await ctx.decodeAudioData(await res.arrayBuffer());
          if (run.stopped) return;
          play(decoded); arrived();
          return;
        }
        if (!res.body?.getReader) {
          const all = new Uint8Array(await res.arrayBuffer());
          if (run.stopped) return;
          play(pcmToBuffer(all)); arrived();
          return;
        }
        const reader = res.body.getReader();
        let carry = null;
        for (;;) {
          const { done: end, value } = await reader.read();
          if (run.stopped) { try { await reader.cancel(); } catch (_) { /* gone */ } return; }
          if (end) break;
          let bytes = value;
          if (carry) { const m = new Uint8Array(carry.length + bytes.length); m.set(carry); m.set(bytes, carry.length); bytes = m; carry = null; }
          if (bytes.length % 2) { carry = bytes.slice(bytes.length - 1); bytes = bytes.subarray(0, bytes.length - 1); }
          if (bytes.length) play(pcmToBuffer(bytes));
        }
        arrived();
      } catch (e) {
        if (!run.stopped) finish('failed');
      }
    })();
    return done;
  }

  return {
    speak: (args, opts) => begin(args, opts, false),
    enqueue: (args, opts) => begin(args, opts, true),
    stop, unlock,
    get speaking() { return speaking; },
    forget(key) { spoken.delete(String(key)); },
  };
}
